import { useEffect, useState } from 'react'
import { useAuth } from '@/lib/auth'
import { supabase } from '@/lib/supabase'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { formatCurrency } from '@/lib/utils'
import { format } from 'date-fns'
import { Plane, Building, Package } from 'lucide-react'

interface Order {
  id: string
  cart_id: string
  status: string
  total_amount: number
  currency: string
  customer_email: string
  customer_name: string
  metadata: any
  created_at: string
  order_items: OrderItem[]
}

interface OrderItem {
  id: string
  item_type: string
  item_data: any
  quantity: number
  unit_price: number
  total_price: number
  status: string
}

export default function PurchasesPage() {
  const { user } = useAuth()
  const [orders, setOrders] = useState<Order[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (user?.id) {
      loadOrders()
    }
  }, [user?.id])

  const loadOrders = async () => {
    try {
      setIsLoading(true)
      console.log('Loading orders for user:', user!.id)

      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          order_items (*)
        `)
        .eq('user_id', user!.id)
        .order('created_at', { ascending: false })

      console.log('Orders query result:', { data, error, count: data?.length })

      if (error) throw error

      setOrders(data || [])
      console.log('Loaded orders:', data)
    } catch (err) {
      console.error('Failed to load orders:', err)
      setError(err instanceof Error ? err.message : 'Failed to load orders')
    } finally {
      setIsLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'created':
      case 'pending':
        return 'bg-blue-100 text-blue-800'
      case 'payment_authorized':
      case 'booking_in_progress':
        return 'bg-yellow-100 text-yellow-800'
      case 'completed':
        return 'bg-green-100 text-green-800'
      case 'failed':
      case 'cancelled':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getItemIcon = (itemType: string) => {
    switch (itemType) {
      case 'flight':
        return <Plane className="h-4 w-4" />
      case 'hotel':
        return <Building className="h-4 w-4" />
      default:
        return <Package className="h-4 w-4" />
    }
  }

  if (isLoading) {
    return (
      <div className="container mx-auto py-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-semibold mb-6">My Trips</h1>
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading orders...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto py-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-semibold mb-6">My Trips</h1>
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <p className="text-red-800">{error}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-6">
      <div className="max-w-4xl mx-auto space-y-6">
        <h1 className="text-3xl font-semibold">My Trips</h1>

        {orders.length === 0 ? (
          <Card>
            <CardHeader>
              <CardTitle>No Trips Yet</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                You haven't booked any trips yet. Start planning your next adventure!
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <Card key={order.id}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">
                        Order #{order.id.slice(0, 8)}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        {format(new Date(order.created_at), 'PPP')}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge className={getStatusColor(order.status)}>
                        {order.status.replace('_', ' ').toUpperCase()}
                      </Badge>
                      {order.metadata?.test_mode && (
                        <Badge className="ml-2 bg-yellow-100 text-yellow-800">
                          TEST MODE
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {order.metadata?.note && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-800">
                      {order.metadata.note}
                    </div>
                  )}

                  <div className="space-y-2">
                    <h4 className="font-medium text-sm">Order Items:</h4>
                    {order.order_items.map((item) => (
                      <div
                        key={item.id}
                        className="flex justify-between items-center p-3 bg-muted/50 rounded"
                      >
                        <div className="flex items-center gap-2">
                          {getItemIcon(item.item_type)}
                          <span className="capitalize">{item.item_type}</span>
                          <span className="text-muted-foreground">x{item.quantity}</span>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">
                            {formatCurrency(item.total_price)}
                          </p>
                          <Badge variant="outline" className="text-xs">
                            {item.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-between items-center pt-2 border-t">
                    <span className="font-semibold">Total:</span>
                    <span className="text-xl font-bold">
                      {formatCurrency(order.total_amount)}
                    </span>
                  </div>

                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>Customer: {order.customer_name}</p>
                    <p>Email: {order.customer_email}</p>
                    {order.cart_id && <p>Cart ID: {order.cart_id}</p>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
